import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import Icon from 'components/Label';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  formControl: {
    width: '100%',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'flex-start',
  },
  label: {
    color: ({ disabled }) => {
      if (disabled) {
        return theme.palette.text.disabled;
      } else {
        return theme.palette.text.primary;
      }
    },
  },
  formGroup: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'row',
    color: theme.palette.text.primary,
  },
  helperText: {
    color: theme.palette.text.primary,
  },
  formControlLabel: {
    display: 'flex',
  },
  checkbox: {
    color: theme.palette.primary.light,
  },
  icon: {
    width: '1.2rem',
    height: '1.2rem',
    color: ({ disabled }) => {
      if (disabled) {
        return theme.palette.text.disabled;
      } else {
        return theme.palette.text.primary;
      }
    },
  },
  hide: {
    display: 'none',
  },
}));

const CheckboxesGroup = ({
  label,
  className,
  required,
  value,
  options,
  onChange: handleChange,
  error,
  disabled,
  info,
  hide,
  formGroupClassName,
  noOptionsText,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ disabled });

  const onChange = event => {
    const checkboxValue = event.target.value;
    const input = [...value];
    if (input.includes(checkboxValue)) {
      const newValue = input.filter(val => val !== checkboxValue);
      if (newValue.length > 0) {
        handleChange(newValue);
      } else {
        handleChange();
      }
    } else {
      input.push(checkboxValue);
      handleChange(input);
    }
  };
  return (
    <FormControl
      className={cn(classes.formControl, className, { [classes.hide]: hide })}
      required={required}
      disabled={disabled}
    >
      <InputLabel shrink className={cn(classes.label, classes.input)}>
        {t(label)}
        {info && <Tooltip text={info} />}
      </InputLabel>

      <FormGroup className={cn(classes.formGroup, formGroupClassName)}>
        {options.map((opt, key) => (
          <FormControlLabel
            key={key}
            className={classes.formControlLabel}
            control={
              <Checkbox
                checked={value.includes(opt.id)}
                color="default"
                className={classes.checkbox}
                onChange={onChange}
                value={opt.id}
              />
            }
            label={
              <Icon
                className={classes.icon}
                label={startCase(opt.name)}
                icon={opt.icon}
              />
            }
          />
        ))}
        {Array.isArray(options) && options.length < 1 && noOptionsText && (
          <Icon label={noOptionsText} />
        )}
      </FormGroup>
      {error && (
        <FormHelperText id="component-error-text" error={Boolean(error)}>
          {t(error)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

CheckboxesGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool,
  info: InfoPropTypes,
  hide: PropTypes.bool,
  formGroupClassName: PropTypes.string,
  noOptionsText: PropTypes.string,
};

CheckboxesGroup.defaultProps = {
  value: [],
};

export default CheckboxesGroup;
